import React, { useState, useEffect } from 'react';
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  VStack,
  Heading,
  IconButton,
  Spinner,
  Center,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
  Tag
} from '@chakra-ui/react';
import { EditIcon, ChevronRightIcon } from '@chakra-ui/icons';
import Header from '../../components/Header';
import BoxWrapper from '../../components/BoxWrapper/BoxWrapper';
import usePagePermissions from '../../hooks/usePagePermissions';

const Roles = () => {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [permissionGroups, setPermissionGroups] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loading, setLoading] = useState(false);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [roleName, setRoleName] = useState('');
  const [editingRoleId, setEditingRoleId] = useState(null);

  const getApiHeaders = () => ({
    "x-api-key": localStorage.getItem("api-key"),
    "x-access-token": localStorage.getItem("customer-portal"),
  });

  // Page-level permission check (distinct from `permissionsLoading` above, which
  // gates the unrelated nested permissions-by-group panel further down).
  const { loading: pagePermissionsLoading, can } = usePagePermissions('user-management/roles-permission');

  const listPermission = can("role-list");
  const createPermission = can("role-create");
  const editPermission = can("role-edit");
  const statusPermission = can("role-on-off");

  const fetchRoles = async () => {
    setLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/roles/list`;
      const response = await fetch(url, { headers: getApiHeaders() });
      const data = await response.json();
      if (data.success) {
        setRoles(data.data);
      }
    } catch (error) {
      console.error("Error fetching roles:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPermissionGroups = async () => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission_group/list?parent_id=0`;
      const response = await fetch(url, { headers: getApiHeaders() });
      const data = await response.json();
      if (data.success) {
        setPermissionGroups(data.data);
      }
    } catch (error) {
      console.error("Error fetching permission groups:", error);
    }
  };

  const fetchPermissions = async (groupId, roleId) => {
    setPermissionsLoading(true);
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/list?group_id=${groupId}&role_id=${roleId}`;
      const response = await fetch(url, { headers: getApiHeaders() });
      const data = await response.json();
      if (data.success) {
        setPermissions(data.data);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const openAddModal = () => {
    setModalMode('add');
    setRoleName('');
    setEditingRoleId(null);
    setIsModalOpen(true);
  };

  const openEditModal = (role) => {
    setModalMode('edit');
    setRoleName(role.name);
    setEditingRoleId(role.id);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setRoleName('');
    setEditingRoleId(null);
  };

  const createRole = async () => {
    if (!roleName.trim()) return;
    try {
      const url = `${process.env.REACT_APP_API_URL}/roles/create`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: roleName })
      });
      const data = await response.json();
      if (data.success) {
        closeModal();
        fetchRoles();
      }
    } catch (error) {
      console.error("Error creating role:", error);
    }
  };

  const updateRole = async () => {
    if (!roleName.trim()) return;
    try {
      const url = `${process.env.REACT_APP_API_URL}/roles/update`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ id: editingRoleId, name: roleName })
      });
      const data = await response.json();
      if (data.success) {
        closeModal();
        fetchRoles();
      }
    } catch (error) {
      console.error("Error updating role:", error);
    }
  };

  const handleSave = () => {
    if (modalMode === 'add') {
      createRole();
    } else {
      updateRole();
    }
  };

  const togglePermission = async (permissionId, currentValue) => {
    try {
      const url = `${process.env.REACT_APP_API_URL}/permission/update-permission`;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          ...getApiHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          permission_id: permissionId,
          role_id: selectedRole.id,
          permission: currentValue ? 0 : 1
        })
      });
      const data = await response.json();
      if (data.success) {
        // Update the toggled permission in place instead of refetching the whole
        // list: a refetch flips permissionsLoading, which swaps the Accordion out
        // for a centered spinner and back in — remounting the scroll container
        // and resetting it to the top on every single toggle.
        setPermissions((prev) =>
          prev.map((group) => ({
            ...group,
            permissions: group.permissions.map((perm) =>
              perm.permission_id === permissionId
                ? { ...perm, permission: currentValue ? 0 : 1 }
                : perm
            ),
          }))
        );
      }
    } catch (error) {
      console.error("Error updating permission:", error);
    }
  };

  useEffect(() => {
    if (pagePermissionsLoading || !can('role-list')) return;
    fetchRoles();
    fetchPermissionGroups();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagePermissionsLoading]);

  useEffect(() => {
    if (selectedRole && selectedGroup) {
      fetchPermissions(selectedGroup.id, selectedRole.id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole, selectedGroup]);


   if (pagePermissionsLoading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        </BoxWrapper>
      </>
    );
  }

  if (!listPermission) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Box className='card-style'>
            <Center h="400px">
              <Box textAlign="center">
                <Text fontSize="xl" fontWeight="semibold" color="gray.700" mb={2}>
                  No Access
                </Text>
                <Text fontSize="sm" color="gray.500">
                  You don't have permission to view this sections.
                </Text>
              </Box>
            </Center>
          </Box>
        </BoxWrapper>
      </>
    );
  }

   if (loading) {
    return (
      <>
        <Header />
        <BoxWrapper>
          <Center h="400px">
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        </BoxWrapper>
      </>
    );
  }
  return (
    <>
    <Header/>
      <BoxWrapper>
          <Flex h="calc(100vh - 100px)">
            {/* Roles Sidebar - Column 1 */}
            <Box
              w={{ base: "100%", md: "280px" }}
              bg="white"
              borderRight="1px"
              borderColor="gray.200"
              display="flex"
              flexDirection="column"
              overflow="hidden"
            >
              <Box p={3} borderBottom="1px" borderColor="gray.200">
                <Flex justify="space-between" align="center" mb={1}>
                  <Heading size="md" color="gray.800">Roles</Heading>
                  {createPermission && (
                    <Button bg="var(--brand-primary)" size="sm" onClick={openAddModal} color={"white"}>
                      Add
                    </Button>
                  )}
                </Flex>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Select a role to manage
                </Text>
              </Box>

              <Box flex={1} overflowY="auto" p={4}>
                {loading && !roles.length ? (
                  <Center py={8}>
                    <Spinner color="blue.500" />
                  </Center>
                ) : (
                  <VStack spacing={2} align="stretch">
                    {roles.map((role) => (
                      <Box
                        key={role.id}
                        p={4}
                        borderRadius="lg"
                        cursor="pointer"
                        transition="all 0.2s"
                        bg={selectedRole?.id === role.id ? 'blue.50' : 'white'}
                        border="1px"
                        borderColor={selectedRole?.id === role.id ? 'blue.400' : 'gray.200'}
                        _hover={{
                          bg: selectedRole?.id === role.id ? 'blue.50' : 'gray.50',
                          borderColor: selectedRole?.id === role.id ? 'blue.400' : 'gray.300',
                          transform: 'translateX(2px)'
                        }}
                      >
                        <Flex justify="space-between" align="center">
                          <Box flex={1} onClick={() => setSelectedRole(role)}>
                            <Text fontWeight="semibold" color="gray.900" fontSize="sm">
                              {role.name}
                            </Text>
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              {new Date(role.created_at).toLocaleDateString()}
                            </Text>
                          </Box>
                          {editPermission && (
                            <IconButton
                              icon={<EditIcon />}
                              size="sm"
                              variant="ghost"
                              colorScheme="blue"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditModal(role);
                              }}
                            />
                          )}
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>
            </Box>

            {/* Permission Groups Sidebar - Column 2 */}
            {selectedRole && (
              <Box
                w={{ base: "100%", md: "280px" }}
                bg="white"
                borderRight="1px"
                borderColor="gray.200"
                display={{ base: selectedRole ? "flex" : "none", md: "flex" }}
                flexDirection="column"
                overflow="hidden"
              >
                <Box p={4} borderBottom="1px" borderColor="gray.200">
                  <Heading size="md" color="gray.800" mb={1}>
                    Permission Groups
                  </Heading>
                  <Text fontSize="xs" color="gray.500">
                    for {selectedRole.name}
                  </Text>
                </Box>

                <Box flex={1} overflowY="auto" p={4}>
                  <VStack spacing={2} align="stretch">
                    {permissionGroups.map((group) => (
                      <Box
                        key={group.id}
                        p={4}
                        borderRadius="lg"
                        cursor="pointer"
                        transition="all 0.2s"
                        bg={selectedGroup?.id === group.id ? 'green.50' : 'white'}
                        border="1px"
                        borderColor={selectedGroup?.id === group.id ? 'green.400' : 'gray.200'}
                        _hover={{
                          bg: selectedGroup?.id === group.id ? 'green.50' : 'gray.50',
                          borderColor: selectedGroup?.id === group.id ? 'green.400' : 'gray.300',
                          transform: 'translateX(2px)'
                        }}
                        onClick={() => setSelectedGroup(group)}
                      >
                        <Flex justify="space-between" align="center">
                          <Text fontWeight="medium" color="gray.900" fontSize="sm">
                            {group.name}
                          </Text>
                          <ChevronRightIcon color="gray.400" />
                        </Flex>
                      </Box>
                    ))}
                  </VStack>
                </Box>
              </Box>
            )}

            {/* Permissions Content - Column 3 */}
            <Flex flex={1} direction="column" overflow="hidden">
              {!selectedRole ? (
                <Center flex={1}>
                  <Box textAlign="center" p={8}>
                    <Box
                      as="svg"
                      mx="auto"
                      h="64px"
                      w="64px"
                      color="gray.300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      mb={4}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                      />
                    </Box>
                    <Heading size="lg" mb={2} color="gray.700">
                      Select a Role
                    </Heading>
                    <Text fontSize="sm" color="gray.500">
                      Choose a role from the left sidebar to manage its permissions
                    </Text>
                  </Box>
                </Center>
              ) : !selectedGroup ? (
                <Center flex={1}>
                  <Box textAlign="center" p={8}>
                    <Box
                      as="svg"
                      mx="auto"
                      h="64px"
                      w="64px"
                      color="gray.300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      mb={4}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={1.5}
                        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </Box>
                    <Heading size="lg" mb={2} color="gray.700">
                      Select a Permission Group
                    </Heading>
                    <Text fontSize="sm" color="gray.500">
                      Choose a permission group to view and manage its permissions
                    </Text>
                  </Box>
                </Center>
              ) : (
                <>
                  {/* Permissions Header */}
                  <Box bg="white" borderBottom="1px" borderColor="gray.200" p={3}>
                    <Flex justify="space-between" align="center">
                      <Box>
                        <Flex align="center" gap={2}>
                          <Heading size="md" color="gray.800">
                            {selectedGroup.name}
                          </Heading>
                          <Tag color="var(--brand-primary-hover)" size={"sm"} bg={"var(--brand-tint)"} border={"1px solid var(--brand-tint-strong)"}>
                            {selectedRole.name}
                          </Tag>
                        </Flex>
                        <Text fontSize="sm" color="gray.600">
                          Manage permissions for this section
                        </Text>
                      </Box>
                    </Flex>
                  </Box>

                  {/* Permissions List with Groups */}
                  <Box flex={1} overflowY="auto" p={6}>
                    {permissionsLoading ? (
                      <Center py={12}>
                        <VStack spacing={4}>
                          <Spinner size="xl" color="blue.500" thickness="4px" />
                          <Text color="gray.600">Loading permissions...</Text>
                        </VStack>
                      </Center>
                    ) : permissions.length > 0 ? (
                      <Accordion allowMultiple defaultIndex={permissions.map((_, i) => i)}>
                        {permissions.map((group) => (
                          <AccordionItem
                            key={group.group_id}
                            border="1px"
                            borderColor="gray.200"
                            borderRadius="xl"
                            mb={4}
                            overflow="hidden"
                          >
                            <AccordionButton
                              bg="gray.50"
                              _hover={{ bg: 'gray.100' }}
                              _expanded={{ bg: 'var(--brand-tint)', borderColor: 'blue.200' }}
                              p={4}
                            >
                              <Box flex="1" textAlign="left">
                                <Flex align="center" gap={2}>
                                  <Text fontWeight="500" color="gray.800" fontSize="lg">
                                    {group.group_name}
                                  </Text>
                                  <Tag color="var(--brand-primary-hover)" size={"sm"} bg={"var(--brand-tint)"} border={"1px solid var(--brand-tint-strong)"}>
                                    {group.permissions.length} permissions
                                  </Tag>
                                </Flex>
                              </Box>
                              <AccordionIcon />
                            </AccordionButton>
                            <AccordionPanel p={4} bg="white">
                              <VStack spacing={3} align="stretch">
                                {group.permissions.map((perm) => (
                                  <Box
                                    key={perm.permission_id}
                                    bg="white"
                                    p={3}
                                    borderRadius="xl"
                                    border="1px"
                                    borderColor="gray.200"
                                    transition="all 0.2s"
                                    _hover={{
                                      shadow: 'sm',
                                      borderColor: 'gray.300'
                                    }}
                                  >
                                    <Flex justify="space-between" align="center">
                                      <Box flex={1}>
                                        <Flex align="center" gap={3}>
                                          <Box
                                            w="8px"
                                            h="8px"
                                            borderRadius="full"
                                            bg={perm.permission ? 'green.400' : 'gray.300'}
                                          />
                                          <Box>
                                            <Text fontWeight="semibold" color="gray.900" fontSize="md">
                                              {perm.name}
                                            </Text>
                                            {perm.description && (
                                              <Text fontSize="sm" color="gray.500" mt={1}>
                                                {perm.description}
                                              </Text>
                                            )}
                                          </Box>
                                        </Flex>
                                      </Box>
                                      <Switch
                                        colorScheme="green"
                                        size="sm"
                                        isChecked={perm.permission}
                                        onChange={() => togglePermission(perm.permission_id, perm.permission)}
                                        disabled={!statusPermission}
                                      />
                                    </Flex>
                                  </Box>
                                ))}
                              </VStack>
                            </AccordionPanel>
                          </AccordionItem>
                        ))}
                      </Accordion>
                    ) : (
                      <Center py={12}>
                        <Box textAlign="center">
                          <Box
                            as="svg"
                            mx="auto"
                            h="48px"
                            w="48px"
                            color="gray.300"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            mb={4}
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={1.5}
                              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                            />
                          </Box>
                          <Heading size="md" color="gray.600" mb={2}>
                            No Permissions Found
                          </Heading>
                          <Text fontSize="sm" color="gray.500">
                            This permission group doesn't have any permissions yet
                          </Text>
                        </Box>
                      </Center>
                    )}
                  </Box>
                </>
              )}
            </Flex>
          </Flex>
      </BoxWrapper>

      {/* Add/Edit Role Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} isCentered>
        <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(10px)" />
        <ModalContent>
          <ModalHeader>
            {modalMode === 'add' ? 'Add New Role' : 'Edit Role'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <FormControl>
              <FormLabel>Role Name</FormLabel>
              <Input
                value={roleName}
                onChange={(e) => setRoleName(e.target.value)}
                placeholder="Enter role name"
                autoFocus
                size="lg"
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={closeModal}>
              Cancel
            </Button>
            <Button
              colorScheme="blue"
              onClick={handleSave}
              isDisabled={!roleName.trim()}
            >
              {modalMode === 'add' ? 'Create Role' : 'Update Role'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Roles;